import { useMemo, useState } from 'react';

import { ApiError, api } from '../../api/client';
import type {
  AvailabilityStatus,
  Person,
  PreferenceLevel,
  RolePairPreference,
  RuleCreate,
} from '../../api/types';
import { Banner, Card, Empty, formatDate, secondaryLabel } from '../../components/ui';
import { useAsync } from '../../hooks/useAsync';
import { useAuth } from '../../hooks/useAuth';

const PREFERENCE_LABELS: Record<PreferenceLevel, string> = {
  HIGH: 'Aime beaucoup',
  MEDIUM: 'Indifférent',
  LOW: 'Peu volontiers',
  AVOID: 'Préfère éviter',
};

const PREFERENCE_ORDER: PreferenceLevel[] = ['HIGH', 'MEDIUM', 'LOW', 'AVOID'];

/**
 * Fiche d'une personne : identité, équipes, compétences, préférences,
 * combinaisons de rôles, disponibilités et limites (brief §3, §4, §5, §6).
 *
 * Tout est formulé en mots. Les pondérations du moteur n'apparaissent nulle
 * part (§17).
 */
export default function PersonsScreen() {
  const { isAdmin } = useAuth();
  const [selected, setSelected] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const teams = useAsync(() => api.teams(), []);
  const roles = useAsync(() => api.roles(), []);
  const persons = useAsync(() => api.persons(), []);

  const guard = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await action();
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.detail : 'Action impossible.');
    } finally {
      setBusy(false);
    }
  };

  const person = (persons.data ?? []).find((item) => item.id === selected) ?? null;

  return (
    <>
      <h1>Personnes</h1>
      {error && <Banner kind="error">{error}</Banner>}
      {notice && <Banner kind="success">{notice}</Banner>}

      <div className="two-columns">
        <Card title="Équipe">
          <CreatePerson
            teams={teams.data ?? []}
            busy={busy}
            onCreate={(body) =>
              guard(async () => {
                const created = await api.createPerson(body);
                persons.reload();
                setSelected(created.id);
              })
            }
          />
          {persons.loading ? (
            <p className="muted">Chargement…</p>
          ) : (persons.data ?? []).length === 0 ? (
            <Empty>Aucune personne enregistrée.</Empty>
          ) : (
            <div className="stack">
              {(persons.data ?? []).map((item) => (
                <button
                  key={item.id}
                  className={selected === item.id ? 'primary' : 'ghost'}
                  style={{ textAlign: 'left' }}
                  onClick={() => setSelected(item.id)}
                >
                  {item.first_name} {item.last_name}
                  {!item.is_active && <span className="badge optional"> inactive</span>}
                </button>
              ))}
            </div>
          )}
        </Card>

        <div>
          {person === null ? (
            <Card>
              <Empty>Choisissez une personne.</Empty>
            </Card>
          ) : (
            <>
              <PersonForm
                key={person.id}
                person={person}
                teams={teams.data ?? []}
                busy={busy}
                canDelete={isAdmin}
                onSave={(changes) =>
                  guard(async () => {
                    await api.updatePerson(person.id, changes);
                    persons.reload();
                    setNotice('Fiche enregistrée.');
                  })
                }
                onDelete={() =>
                  guard(async () => {
                    await api.deletePerson(person.id);
                    setSelected(null);
                    persons.reload();
                    setNotice('Personne supprimée.');
                  })
                }
              />
              <SkillsAndPreferences
                personId={person.id}
                roles={roles.data ?? []}
                busy={busy}
                guard={guard}
              />
              <RolePairs
                personId={person.id}
                roles={roles.data ?? []}
                busy={busy}
                guard={guard}
              />
              <PersonAvailability personId={person.id} busy={busy} guard={guard} />
              <PersonRules
                personId={person.id}
                roles={roles.data ?? []}
                busy={busy}
                guard={guard}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
}

// --------------------------------------------------------------------------- //

interface Team {
  id: number;
  name: string;
}

function TeamChips({
  teams,
  selected,
  onToggle,
}: {
  teams: Team[];
  selected: number[];
  onToggle: (id: number) => void;
}) {
  return (
    <div className="slot">
      {teams.map((team) => (
        <button
          key={team.id}
          type="button"
          className="chip"
          aria-pressed={selected.includes(team.id)}
          style={
            selected.includes(team.id)
              ? { borderColor: 'var(--accent)', fontWeight: 600 }
              : undefined
          }
          onClick={() => onToggle(team.id)}
        >
          {team.name}
        </button>
      ))}
    </div>
  );
}

function CreatePerson({
  teams,
  busy,
  onCreate,
}: {
  teams: Team[];
  busy: boolean;
  onCreate: (body: {
    first_name: string;
    last_name: string;
    team_ids: number[];
  }) => Promise<void>;
}) {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [teamIds, setTeamIds] = useState<number[]>([]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await onCreate({ first_name: firstName, last_name: lastName, team_ids: teamIds });
    setFirstName('');
    setLastName('');
  };

  return (
    <form className="stack" onSubmit={submit} style={{ marginBottom: 16 }}>
      <div className="row">
        <div style={{ flex: 1 }}>
          <label htmlFor="first">Prénom</label>
          <input
            id="first"
            required
            style={{ width: '100%' }}
            value={firstName}
            onChange={(event) => setFirstName(event.target.value)}
          />
        </div>
        <div style={{ flex: 1 }}>
          <label htmlFor="last">Nom</label>
          <input
            id="last"
            required
            style={{ width: '100%' }}
            value={lastName}
            onChange={(event) => setLastName(event.target.value)}
          />
        </div>
      </div>
      <div>
        <label>Équipes</label>
        <TeamChips
          teams={teams}
          selected={teamIds}
          onToggle={(id) =>
            setTeamIds((previous) =>
              previous.includes(id)
                ? previous.filter((value) => value !== id)
                : [...previous, id],
            )
          }
        />
      </div>
      <button className="primary" type="submit" disabled={busy || teamIds.length === 0}>
        Ajouter la personne
      </button>
    </form>
  );
}

function PersonForm({
  person,
  teams,
  busy,
  canDelete,
  onSave,
  onDelete,
}: {
  person: Person;
  teams: Team[];
  busy: boolean;
  canDelete: boolean;
  onSave: (changes: Record<string, unknown>) => Promise<void>;
  onDelete: () => Promise<void>;
}) {
  const [draft, setDraft] = useState({
    first_name: person.first_name,
    last_name: person.last_name,
    email: person.email ?? '',
    phone: person.phone ?? '',
    max_roles_per_event: person.max_roles_per_event ?? 1,
    is_active: person.is_active,
    // `team_ids` est facultatif dans le schéma : une personne peut n'être
    // rattachée à aucune équipe tant qu'un administrateur ne l'a pas fait.
    team_ids: person.team_ids ?? [],
  });
  const [confirming, setConfirming] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    await onSave({
      ...draft,
      email: draft.email || null,
      phone: draft.phone || null,
    });
  };

  return (
    <Card title="Fiche">
      <form className="stack" onSubmit={submit}>
        <div className="row">
          <div style={{ flex: 1 }}>
            <label htmlFor="edit-first">Prénom</label>
            <input
              id="edit-first"
              required
              style={{ width: '100%' }}
              value={draft.first_name}
              onChange={(event) =>
                setDraft((d) => ({ ...d, first_name: event.target.value }))
              }
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="edit-last">Nom</label>
            <input
              id="edit-last"
              required
              style={{ width: '100%' }}
              value={draft.last_name}
              onChange={(event) =>
                setDraft((d) => ({ ...d, last_name: event.target.value }))
              }
            />
          </div>
        </div>
        <div className="row">
          <div style={{ flex: 2 }}>
            <label htmlFor="edit-email">Adresse e-mail</label>
            <input
              id="edit-email"
              type="email"
              style={{ width: '100%' }}
              value={draft.email}
              onChange={(event) => setDraft((d) => ({ ...d, email: event.target.value }))}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label htmlFor="edit-phone">Téléphone</label>
            <input
              id="edit-phone"
              style={{ width: '100%' }}
              value={draft.phone}
              onChange={(event) => setDraft((d) => ({ ...d, phone: event.target.value }))}
            />
          </div>
        </div>
        <div>
          <label>Équipes</label>
          <TeamChips
            teams={teams}
            selected={draft.team_ids}
            onToggle={(id) =>
              setDraft((d) => ({
                ...d,
                team_ids: d.team_ids.includes(id)
                  ? d.team_ids.filter((value) => value !== id)
                  : [...d.team_ids, id],
              }))
            }
          />
        </div>
        <div className="row">
          <div>
            <label htmlFor="edit-max">Rôles tenus en même temps, au plus</label>
            <input
              id="edit-max"
              type="number"
              min={1}
              max={5}
              style={{ width: 90 }}
              value={draft.max_roles_per_event}
              onChange={(event) =>
                setDraft((d) => ({
                  ...d,
                  max_roles_per_event: Number(event.target.value),
                }))
              }
            />
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 18 }}>
            <input
              type="checkbox"
              checked={draft.is_active}
              onChange={(event) =>
                setDraft((d) => ({ ...d, is_active: event.target.checked }))
              }
            />
            Compte actif (une personne inactive n'est plus proposée)
          </label>
        </div>

        <div className="spread">
          <button className="primary" type="submit" disabled={busy}>
            Enregistrer
          </button>
          {canDelete &&
            (confirming ? (
              <span className="row" style={{ alignItems: 'center' }}>
                <span className="muted">Supprimer définitivement ?</span>
                <button
                  type="button"
                  className="small"
                  disabled={busy}
                  onClick={() => {
                    setConfirming(false);
                    void onDelete();
                  }}
                >
                  Oui, supprimer
                </button>
                <button
                  type="button"
                  className="ghost small"
                  onClick={() => setConfirming(false)}
                >
                  Annuler
                </button>
              </span>
            ) : (
              <button
                type="button"
                className="ghost small"
                onClick={() => setConfirming(true)}
              >
                Supprimer
              </button>
            ))}
        </div>
        <p className="muted">
          Une personne qui figure dans un planning publié ne peut pas être supprimée :
          désactivez-la plutôt, l'historique reste alors intact.
        </p>
      </form>
    </Card>
  );
}

interface Role {
  id: number;
  name: string;
}

type Guard = (action: () => Promise<unknown>) => Promise<void>;

function SkillsAndPreferences({
  personId,
  roles,
  busy,
  guard,
}: {
  personId: number;
  roles: Role[];
  busy: boolean;
  guard: Guard;
}) {
  const skills = useAsync(() => api.skills(personId), [personId]);
  const preferences = useAsync(() => api.preferences(personId), [personId]);

  const hasSkill = (roleId: number) =>
    (skills.data ?? []).some((item) => item.role_id === roleId);
  const preferenceOf = (roleId: number): PreferenceLevel | '' =>
    (preferences.data ?? []).find((item) => item.role_id === roleId)?.preference_level ??
    '';

  return (
    <Card title="Ce qu'elle sait faire, et ce qu'elle préfère">
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Rôle</th>
              <th>Sait faire</th>
              <th>Préférence</th>
            </tr>
          </thead>
          <tbody>
            {roles.map((role) => (
              <tr key={role.id}>
                <td>{role.name}</td>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`${role.name} : sait faire`}
                    checked={hasSkill(role.id)}
                    disabled={busy}
                    onChange={() =>
                      guard(async () => {
                        const next = hasSkill(role.id)
                          ? (skills.data ?? []).filter((item) => item.role_id !== role.id)
                          : [
                              ...(skills.data ?? []),
                              { role_id: role.id, skill_level: 'CONFIRMED' as const },
                            ];
                        await api.replaceSkills(personId, next);
                        skills.reload();
                      })
                    }
                  />
                </td>
                <td>
                  <select
                    aria-label={`${role.name} : préférence`}
                    value={preferenceOf(role.id)}
                    disabled={busy || !hasSkill(role.id)}
                    onChange={(event) =>
                      guard(async () => {
                        const level = event.target.value as PreferenceLevel | '';
                        const others = (preferences.data ?? []).filter(
                          (item) => item.role_id !== role.id,
                        );
                        await api.replacePreferences(
                          personId,
                          level === ''
                            ? others
                            : [...others, { role_id: role.id, preference_level: level }],
                        );
                        preferences.reload();
                      })
                    }
                  >
                    <option value="">Non précisé</option>
                    {PREFERENCE_ORDER.map((level) => (
                      <option key={level} value={level}>
                        {PREFERENCE_LABELS[level]}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="muted" style={{ marginTop: 10 }}>
        « Préfère éviter » ne veut pas dire « ne sait pas faire » : la personne pourra
        être affectée s'il n'y a pas d'autre solution.
      </p>
    </Card>
  );
}

/**
 * Préférences de **combinaisons** de rôles (brief §3 et §6).
 *
 * Seules les combinaisons autorisées et que la personne sait tenir sont
 * proposées : lui demander son avis sur une combinaison impossible n'aurait
 * aucun sens.
 */
function RolePairs({
  personId,
  roles,
  busy,
  guard,
}: {
  personId: number;
  roles: Role[];
  busy: boolean;
  guard: Guard;
}) {
  const skills = useAsync(() => api.skills(personId), [personId]);
  const compatibility = useAsync(() => api.roleCompatibility(), []);
  const pairs = useAsync(() => api.rolePairs(personId), [personId]);

  const roleName = useMemo(() => {
    const index = new Map(roles.map((role) => [role.id, role.name]));
    return (roleId: number) => index.get(roleId) ?? `rôle ${roleId}`;
  }, [roles]);

  const candidates = useMemo(() => {
    const known = new Set((skills.data ?? []).map((item) => item.role_id));
    return (compatibility.data ?? []).filter(
      (pair) => known.has(pair.role_a_id) && known.has(pair.role_b_id),
    );
  }, [skills.data, compatibility.data]);

  const levelOf = (pair: { role_a_id: number; role_b_id: number }): PreferenceLevel | '' =>
    (pairs.data ?? []).find(
      (item) => item.role_a_id === pair.role_a_id && item.role_b_id === pair.role_b_id,
    )?.preference_level ?? '';

  return (
    <Card title="Rôles tenus en même temps">
      {candidates.length === 0 ? (
        <Empty>
          Aucune combinaison possible pour l'instant : il faut au moins deux rôles connus
          et déclarés compatibles entre eux.
        </Empty>
      ) : (
        <>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th>Combinaison</th>
                  <th>Préférence</th>
                </tr>
              </thead>
              <tbody>
                {candidates.map((pair) => {
                  const label = `${roleName(pair.role_a_id)} + ${roleName(pair.role_b_id)}`;
                  return (
                    <tr key={`${pair.role_a_id}-${pair.role_b_id}`}>
                      <td>{label}</td>
                      <td>
                        <select
                          aria-label={`${label} : préférence`}
                          value={levelOf(pair)}
                          disabled={busy}
                          onChange={(event) =>
                            guard(async () => {
                              const level = event.target.value as PreferenceLevel | '';
                              const others = (pairs.data ?? []).filter(
                                (item) =>
                                  !(
                                    item.role_a_id === pair.role_a_id &&
                                    item.role_b_id === pair.role_b_id
                                  ),
                              );
                              const next: RolePairPreference[] =
                                level === ''
                                  ? others
                                  : [
                                      ...others,
                                      {
                                        role_a_id: pair.role_a_id,
                                        role_b_id: pair.role_b_id,
                                        preference_level: level,
                                      },
                                    ];
                              await api.replaceRolePairs(personId, next);
                              pairs.reload();
                            })
                          }
                        >
                          <option value="">Non précisé</option>
                          {PREFERENCE_ORDER.map((level) => (
                            <option key={level} value={level}>
                              {PREFERENCE_LABELS[level]}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="muted" style={{ marginTop: 10 }}>
            Cette préférence porte sur la <strong>combinaison</strong>, pas sur chaque
            rôle pris séparément : on peut préférer le piano en général, et la guitare
            quand on mène déjà le chant.
          </p>
        </>
      )}
    </Card>
  );
}

/**
 * Disponibilités saisies par le responsable, pour les personnes qui ne se
 * connectent pas (ambiguïté A9 : beaucoup de membres n'auront jamais de compte).
 */
function PersonAvailability({
  personId,
  busy,
  guard,
}: {
  personId: number;
  busy: boolean;
  guard: Guard;
}) {
  const events = useAsync(() => api.events(new Date().toISOString().slice(0, 10)), []);
  const answers = useAsync(() => api.availability(personId), [personId]);

  const statusOf = (eventId: number): AvailabilityStatus =>
    (answers.data ?? []).find((row) => row.event_id === eventId)?.status ?? 'UNKNOWN';

  const set = (eventId: number, status: AvailabilityStatus) =>
    guard(async () => {
      await api.replaceAvailability(personId, [{ event_id: eventId, status, note: null }]);
      answers.reload();
    });

  const upcoming = events.data ?? [];

  return (
    <Card title="Ses disponibilités">
      {upcoming.length === 0 ? (
        <Empty>Aucune date à venir.</Empty>
      ) : (
        <div className="stack">
          {upcoming.map((event) => (
            <div key={event.id}>
              <div className="spread" style={{ marginBottom: 6 }}>
                <strong>{formatDate(event.starts_at)}</strong>
                <span className="muted">
                  {secondaryLabel(event.title, event.starts_at)}
                </span>
              </div>
              <div className="choice-group" role="group" aria-label={event.title}>
                {(
                  [
                    ['AVAILABLE', 'Disponible', 'yes'],
                    ['UNAVAILABLE', 'Indisponible', 'no'],
                    ['UNKNOWN', 'Sans réponse', 'maybe'],
                  ] as const
                ).map(([status, label, tone]) => (
                  <button
                    key={status}
                    type="button"
                    className={`choice ${tone}`}
                    disabled={busy}
                    aria-pressed={statusOf(event.id) === status}
                    onClick={() => set(event.id, status)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
      <p className="muted" style={{ marginTop: 10 }}>
        À saisir pour les personnes qui ne se connectent pas. « Sans réponse » n'est pas
        un refus : le moteur peut quand même les proposer, en les plaçant après celles
        qui ont confirmé.
      </p>
    </Card>
  );
}

function PersonRules({
  personId,
  roles,
  busy,
  guard,
}: {
  personId: number;
  roles: Role[];
  busy: boolean;
  guard: Guard;
}) {
  const rules = useAsync(() => api.rules(personId), [personId]);
  const skills = useAsync(() => api.skills(personId), [personId]);

  // Une obligation ne se propose que sur un rôle que la personne sait tenir.
  // « Au moins une fois à la batterie » pour quelqu'un qui n'en joue pas rend
  // simplement le planning impossible : autant ne pas permettre de l'écrire.
  const roleName = (roleId: number) =>
    roles.find((role) => role.id === roleId)?.name ?? `rôle ${roleId}`;
  const known = new Set((skills.data ?? []).map((item) => item.role_id));
  const eligible = roles.filter((role) => known.has(role.id));

  return (
    <Card title="Combien de fois">
      <RuleForm
        busy={busy}
        roles={eligible}
        onAdd={(rule) =>
          guard(async () => {
            await api.createRule(personId, rule);
            rules.reload();
          })
        }
      />
      {(rules.data ?? []).length === 0 ? (
        <p className="muted">Aucune limite particulière.</p>
      ) : (
        <ul className="stack" style={{ listStyle: 'none', padding: 0, marginTop: 12 }}>
          {(rules.data ?? []).map((rule) => (
            <li key={rule.id} className="spread">
              <span>
                {rule.label ??
                  describeRule(
                    rule.rule_type,
                    rule.value,
                    rule.role_id === null ? null : roleName(rule.role_id),
                  )}{' '}
                <span
                  className={`badge ${rule.enforcement === 'HARD' ? 'required' : 'preferred'}`}
                >
                  {enforcementLabel(rule.rule_type, rule.enforcement)}
                </span>
              </span>
              <button
                className="ghost small"
                disabled={busy}
                onClick={() =>
                  guard(async () => {
                    await api.deleteRule(personId, rule.id);
                    rules.reload();
                  })
                }
              >
                Retirer
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

function describeRule(type: string, value: number, role: string | null): string {
  const what = role ? `fois comme ${role}` : 'service(s)';
  if (type === 'MAX_PER_WINDOW') return `Au plus ${value} ${what} par mois`;
  if (type === 'MIN_PER_WINDOW') return `Au moins ${value} ${what} par mois`;
  return 'Éviter deux services rapprochés';
}

/**
 * « Jamais dépassé » n'a aucun sens pour un minimum : on ne dépasse pas un
 * plancher, on y manque. Le même champ se lit donc dans l'autre sens.
 */
function enforcementLabel(type: string, enforcement: string): string {
  if (type === 'MIN_PER_WINDOW') {
    return enforcement === 'HARD' ? 'toujours respecté' : 'si possible';
  }
  return enforcement === 'HARD' ? 'jamais dépassé' : 'si possible';
}

/**
 * Saisie d'une règle en langage humain. Le choix « jamais / si possible » est
 * la distinction du §5 entre contrainte dure et préférence — c'est au
 * responsable de la faire, pas au moteur de la deviner.
 */
function RuleForm({
  onAdd,
  roles,
  busy,
}: {
  onAdd: (rule: RuleCreate) => Promise<void>;
  roles: Role[];
  busy: boolean;
}) {
  const [kind, setKind] = useState<'MAX' | 'MIN' | 'GAP'>('MAX');
  const [value, setValue] = useState(2);
  const [roleId, setRoleId] = useState<number | null>(null);
  // Une obligation déclarée « toujours » peut rendre tout le planning
  // impossible le jour où la personne est absente tout le mois. Le défaut est
  // donc « si possible » : le moteur fera tout pour l'honorer, et dira
  // simplement qu'il n'a pas pu plutôt que de ne rien produire.
  const [strict, setStrict] = useState(true);

  const current = roleId ?? roles[0]?.id ?? null;
  const currentName = roles.find((role) => role.id === current)?.name ?? '';

  const choose = (next: 'MAX' | 'MIN' | 'GAP') => {
    setKind(next);
    if (next === 'MIN') {
      setStrict(false);
      setValue(1);
    } else if (next === 'MAX') {
      setStrict(true);
      setValue(2);
    }
  };

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (kind === 'MIN') {
      if (current === null) return;
      await onAdd({
        rule_type: 'MIN_PER_WINDOW',
        window_type: 'CALENDAR_MONTH',
        value,
        enforcement: strict ? 'HARD' : 'SOFT',
        window_days: null,
        team_id: null,
        role_id: current,
        weight_override: null,
        label: `Au moins ${value} fois comme ${currentName} par mois`,
      });
      return;
    }
    await onAdd(
      kind === 'MAX'
        ? {
            rule_type: 'MAX_PER_WINDOW',
            window_type: 'CALENDAR_MONTH',
            value,
            enforcement: strict ? 'HARD' : 'SOFT',
            window_days: null,
            team_id: null,
            role_id: null,
            weight_override: null,
            label: `Au plus ${value} service(s) par mois`,
          }
        : {
            rule_type: 'MIN_GAP',
            window_type: 'WHOLE_HORIZON',
            value: 1,
            enforcement: strict ? 'HARD' : 'SOFT',
            window_days: null,
            team_id: null,
            role_id: null,
            weight_override: null,
            label: 'Éviter deux dimanches de suite',
          },
    );
  };

  return (
    <>
      <form className="row" onSubmit={submit}>
        <div>
          <label htmlFor="kind">Règle</label>
          <select
            id="kind"
            value={kind}
            onChange={(event) => choose(event.target.value as 'MAX' | 'MIN' | 'GAP')}
          >
            <option value="MAX">Au plus, tous rôles confondus</option>
            <option value="MIN" disabled={roles.length === 0}>
              Au moins, dans un rôle précis
            </option>
            <option value="GAP">Éviter deux dimanches de suite</option>
          </select>
        </div>

        {kind === 'MIN' && (
          <div>
            <label htmlFor="rule-role">Dans le rôle</label>
            <select
              id="rule-role"
              value={current === null ? '' : String(current)}
              onChange={(event) => setRoleId(Number(event.target.value))}
            >
              {roles.map((role) => (
                <option key={role.id} value={String(role.id)}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {kind !== 'GAP' && (
          <div>
            <label htmlFor="value">{kind === 'MIN' ? 'Au moins' : 'Au plus'}</label>
            <input
              id="value"
              type="number"
              min={kind === 'MIN' ? 1 : 0}
              max={31}
              style={{ width: 80 }}
              value={value}
              onChange={(event) => setValue(Number(event.target.value))}
            />
          </div>
        )}

        <div>
          <label htmlFor="strict">Caractère</label>
          <select
            id="strict"
            value={strict ? 'HARD' : 'SOFT'}
            onChange={(event) => setStrict(event.target.value === 'HARD')}
          >
            <option value="HARD">
              {kind === 'MIN' ? 'Toujours respecté' : 'Jamais dépassé'}
            </option>
            <option value="SOFT">Si possible</option>
          </select>
        </div>

        <button className="primary" type="submit" disabled={busy || (kind === 'MIN' && current === null)}>
          Ajouter
        </button>
      </form>

      {kind === 'MIN' && roles.length === 0 && (
        <p className="muted">
          Déclarez d’abord ce que cette personne sait faire : une obligation ne peut
          porter que sur un rôle qu’elle peut tenir.
        </p>
      )}
      {kind === 'MIN' && current !== null && (
        <p className="muted">
          « Au moins {value} fois comme {currentName} par mois ».{' '}
          {strict
            ? 'Déclarée « toujours respectée », cette obligation rendra le planning impossible si la personne est absente tout un mois — le diagnostic le dira, mais aucun planning ne sortira.'
            : 'Déclarée « si possible », le moteur fera tout pour l’honorer et signalera les mois où il n’a pas pu.'}
        </p>
      )}
    </>
  );
}
